import {datesWithinBounds, formatDate, formatSince, formatTime, isMidnight} from "@/lib/time";
import {StyleSheet, Text, View} from "react-native";
import {Color} from "@/lib/styles";
import Icon from "@/lib/components/Icon";

type Props = {
    start: Date,
    end?: Date | null,
    // Gray out dates in the past.
    autoFade?: boolean,
    // Relative time format
    relativeSince?: boolean,
    oneLineOnly?: boolean,
}

export default function Timestamp({start, end, autoFade, relativeSince, oneLineOnly}: Props) {
    const withinBounds = end ? datesWithinBounds(start, end) : false;
    const showStartTime = withinBounds || !isMidnight(start);
    const showEndTime = withinBounds || (end && !isMidnight(end));

    const textColor = (() => {
        if (autoFade === false) {
            return Color.PrimaryText;
        }
        const now = new Date();
        now.setHours(0);
        now.setMinutes(0);
        now.setSeconds(0);
        now.setMilliseconds(0);
        if (end && end < now) {
            return Color.FadedText;
        }
        if (!end && start < now) {
            return Color.FadedText;
        }
        return Color.PrimaryText;
    })();

    const textStyle = [styles.text, {color: textColor}];

    // simplified view for short spans
    if (relativeSince) {
        return (
            <View style={styles.container}>
                <View style={styles.line}>
                    <Icon kind={"clock"} color={textColor}/>
                    <Text style={textStyle}>{formatSince(start)}</Text>
                </View>
            </View>
        )
    } else if (withinBounds) {
        return (
            <View style={styles.container}>
                <View style={styles.line}>
                    <Icon kind={"date"} color={textColor}/>
                    <Text style={textStyle}>{formatDate(start)}</Text>
                    {showStartTime && <Icon kind={"clock"} color={textColor}/>}
                    {showStartTime && <Text style={textStyle}>{formatTime(start)}–{formatTime(end!)}</Text>}
                </View>
            </View>
        )
    } else if (end && !oneLineOnly) {
        return (
            <View style={styles.container}>
                <View style={styles.line}>
                    <Icon kind={"date"} color={textColor}/>
                    <Text style={textStyle}>Fra {formatDate(start)}</Text>
                    {showStartTime && <Icon kind={"clock"} color={textColor}/>}
                    {showStartTime && <Text style={textStyle}>{formatTime(start)}</Text>}
                </View>
                <View style={styles.line}>
                    <Icon kind={"date"} color={textColor}/>
                    <Text style={textStyle}>Til {formatDate(end)}</Text>
                    {showEndTime && <Icon kind={"clock"} color={textColor}/>}
                    {showEndTime && <Text style={textStyle}>{formatTime(end)}</Text>}
                </View>
            </View>
        )
    } else {
        return (
            <View style={styles.container}>
                <View style={styles.line}>
                    <Icon kind={"date"} color={textColor}/>
                    <Text style={textStyle}>{formatDate(start)}</Text>
                    {showStartTime && <Icon kind={"clock"} color={textColor}/>}
                    {showStartTime && <Text style={textStyle}>{formatTime(start)}</Text>}
                </View>
            </View>
        )
    }
}

export const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        gap: 8,
    },
    text: {
        color: Color.PrimaryText,
        fontSize: 16,
        marginRight: 8,
    },
    line: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    }
})